import React, { useState, useRef } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { UniqueIdentifier } from '@dnd-kit/core'
import { Icon } from '@evertel/web/ui'
import { DocumentServiceProvider } from '@evertel/service'
import { WidgetsProvider, Delegator } from '@evertel/schema-parser'
import { WriteWidgets } from '../schema-widgets'

import { DragData, ParsedItem } from './types'
import { ELEMENT_TYPE_LABELS, PLACEHOLDER_ELEMENT_ID, TYPE_ICONS } from './constants'

interface DnDElementProps {
    element: ParsedItem
    rowIndex: number
    schemaId: number
    activeId: UniqueIdentifier | null
    isDraggingElement: boolean
    onDelete: () => void
    onEdit: () => void
}

/**
 * Sortable form element within a row.
 * Renders the actual form field using the legacy Delegator/WriteWidgets system.
 * Shows action menu on hover/focus for editing and deleting.
 */
export const DnDElement: React.FC<DnDElementProps> = ({
    element,
    rowIndex,
    schemaId,
    activeId,
    isDraggingElement: isDraggingAnyElement,
    onDelete,
    onEdit
}) => {
    const [isHovered, setIsHovered] = useState(false)
    const [isFocusWithin, setIsFocusWithin] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const isPlaceholder = element.id === PLACEHOLDER_ELEMENT_ID

    const dragData: DragData = {
        type: 'element',
        elementType: element.type,
        fromToolbox: false,
        rowIndex,
        elementIndex: element.index
    }

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: element.id,
        data: dragData,
        disabled: {
            draggable: isPlaceholder, // Disable dragging for placeholder
            droppable: false // Collision detection handles drop filtering
        }
    })

    // Only apply transform/transition during active drag
    // This prevents the jump-back-then-animate issue when drag ends
    const style = {
        transform: activeId ? CSS.Transform.toString(transform) : undefined,
        transition: activeId ? transition : undefined,
        opacity: isDragging ? 0.5 : 1
    }

    // Get label from element props (title) or fall back to type label
    const label = (element.title as string) || ELEMENT_TYPE_LABELS[element.type] || element.type

    // Show menu when hovered OR when focus is within the element/menu
    const showMenu = (isHovered || isFocusWithin) && !isDragging && !isPlaceholder && !isDraggingAnyElement

    // Handle focus events to track when focus is within the element or its menu
    const handleFocusIn = () => setIsFocusWithin(true)
    const handleFocusOut = (e: React.FocusEvent) => {
        // Check if focus is moving to another element within our container
        if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
            setIsFocusWithin(false)
        }
    }

    // Render placeholder element (dashed border, shows element type)
    if (isPlaceholder) {
        return (
            <div
                ref={setNodeRef}
                style={style}
                className="sb-element sb-element--placeholder"
            >
                <div className="sb-element__container d-flex align-items-center justify-content-center">
                    <Icon
                        name={TYPE_ICONS[element.type] as any || 'cube'}
                        className="me-2 text-primary"
                    />
                    <span className="text-primary small">
                        {ELEMENT_TYPE_LABELS[element.type] || element.type}
                    </span>
                </div>
            </div>
        )
    }

    // Extract props for Delegator (everything except our internal index)
    const { index: _index, ...delegatorProps } = element

    return (
        <div
            ref={(node) => {
                setNodeRef(node)
                ;(containerRef as any).current = node
            }}
            style={style}
            className={`sb-element sb-element--${element.type} ${isDragging ? 'shadow' : ''}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={handleFocusIn}
            onBlur={handleFocusOut}
        >
            <div
                className={`sb-element__container ${isFocusWithin ? 'border-primary' : ''}`}
                {...attributes}
                {...listeners}
                tabIndex={0}
                role="button"
                aria-label={`${label} - ${ELEMENT_TYPE_LABELS[element.type]}. Press Space or Enter to drag, Tab to access actions.`}
            >
                {/* Element Content - rendered via legacy Delegator */}
                <div className="sb-element__content pe-none">
                    <DocumentServiceProvider services={[{
                        id: 'meta', value: { scope: 'schemaBuilder' }
                    }]}>
                        <WidgetsProvider value={WriteWidgets}>
                            <Delegator
                                index={element.index.toString()}
                                scope="schemaBuilder"
                                schemaId={schemaId}
                                {...delegatorProps}
                            />
                        </WidgetsProvider>
                    </DocumentServiceProvider>
                </div>

                {/* Action Menu - visible on hover or focus */}
                {showMenu && (
                    <div className="sb-element__hover-menu">
                        <button
                            className="btn btn-sm btn-outline-primary py-0 px-1"
                            onClick={(e) => {
                                e.stopPropagation()
                                onEdit()
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    onEdit()
                                }
                            }}
                            title="Edit element"
                            aria-label={`Edit ${label}`}
                        >
                            <Icon name="pencil" />
                        </button>
                        <button
                            className="btn btn-sm btn-outline-danger py-0 px-1"
                            onClick={(e) => {
                                e.stopPropagation()
                                onDelete()
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    onDelete()
                                }
                            }}
                            title="Delete element"
                            aria-label={`Delete ${label}`}
                        >
                            <Icon name="trash-can" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
